import { Menu } from 'lucide-react';
import { NavProvider, useNav } from './NavContext';
import AppSidebar from './AppSidebar';

/**
 * Thin header strip shown only on mobile (md:hidden).
 * Desktop has no top bar — the sidebar covers the full height.
 */
const MobileHeader = () => {
  const { toggleMobileSidebar } = useNav();
  const isClient = sessionStorage.getItem('role')?.toLowerCase() === 'client';
  return (
    <header className={`md:hidden fixed top-0 left-0 right-0 z-50 h-12 border-b flex items-center px-3 gap-3
      ${isClient ? 'bg-gray-900 border-gray-700/50' : 'bg-white border-gray-100'}`}>
      <button
        onClick={toggleMobileSidebar}
        className={`p-1.5 rounded-md transition-colors
          ${isClient ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
        aria-label="Open navigation"
      >
        <Menu size={20} />
      </button>
      <img src="/capgemini-logo.png" alt="Capgemini" className="h-7 object-contain" />
    </header>
  );
};

/**
 * Inner layout — consumes NavContext to drive the content margin.
 *
 * Desktop:
 *   Sidebar is full-height, no top bar → no vertical offset needed.
 *   Expanded  sidebar (w-60): main gets md:ml-60
 *   Collapsed sidebar (w-16): main gets md:ml-16
 *
 * Mobile:
 *   Sidebar is an overlay → no margin on main (ml-0).
 *   MobileHeader is h-12 → main gets pt-12 to clear it.
 */
const LayoutInner = ({ children }) => {
  const { sidebarCollapsed, sidebarHoverExpanded } = useNav();
  const isExpanded = !sidebarCollapsed || sidebarHoverExpanded;

  return (
    <>
      <MobileHeader />
      <AppSidebar />
      <main
        className={`
          h-screen overflow-y-auto
          transition-[margin] duration-200
          pt-12 md:pt-0
          ml-0
          ${isExpanded ? 'md:ml-60' : 'md:ml-16'}
        `}
      >
        {children}
      </main>
    </>
  );
};

const AppLayout = ({ children }) => (
  <NavProvider>
    <LayoutInner>{children}</LayoutInner>
  </NavProvider>
);

export default AppLayout;
